import React, { useState, useEffect } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Keyboard, Mousewheel, Zoom } from 'swiper/modules';
import { motion, AnimatePresence } from 'framer-motion';
import Lenis from 'lenis';
import { Helmet } from 'react-helmet';

import './slider-freemode.scss'
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/keyboard';
import 'swiper/css/zoom';


import './PortraitsSquare.scss'


import Header from '../components/Header.js'
import { getThumbnailUrl, getFullScreenUrl, getThumbnailDimensions, isVideo, getVideoUrl } from '../utils/imageLoader.js';


const Portraits = ({ portraits, loading, buttons }) => {
    const [, setWindowWidth] = useState(window.innerWidth);
    const [imageDimensions, setImageDimensions] = useState({});
    const [currentSlide, setCurrentSlide] = useState(1);
    const [activePortrait, setActivePortrait] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [hoveredImageId, setHoveredImageId] = useState(null);
    const [initialSlide, setInitialSlide] = useState(0);
    const [modalReady, setModalReady] = useState(false);
    const [modalSwiper, setModalSwiper] = useState(null);
    const [modalImagesLoaded, setModalImagesLoaded] = useState({});

    useEffect(() => {
        document.title = "Portraits • Alex de Brabant";
    }, []);

    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Smooth/slow scroll only on Portraits page
    useEffect(() => {
        const lenis = new Lenis({
            smoothWheel: true,
            wheelMultiplier: 0.8,
            touchMultiplier: 0.9,
        });

        let rafId;
        const raf = (time) => {
            lenis.raf(time);
            rafId = requestAnimationFrame(raf);
        };
        rafId = requestAnimationFrame(raf);

        return () => {
            cancelAnimationFrame(rafId);
            lenis.destroy();
        };
    }, []);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
            // Recalculate all text positions after resize
            const images = document.querySelectorAll('.portrait-img-square');
            images.forEach((img, index) => {
                handleImageLoad({ target: img }, index);
            });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Add effect to handle back button
    useEffect(() => {
        const handleBackButton = (e) => {
            if (isModalOpen) {
                e.preventDefault();
                setIsModalOpen(false);
            }
        };

        window.addEventListener('popstate', handleBackButton);
        return () => window.removeEventListener('popstate', handleBackButton);
    }, [isModalOpen]);

    useEffect(() => {
        if (isModalOpen) {
            document.body.style.overflow = 'hidden';
            document.body.style.overscrollBehaviorX = 'contain';
            document.body.style.touchAction = 'pan-y pinch-zoom';
        } else {
            document.body.style.overflow = 'auto';
            document.body.style.overscrollBehaviorX = '';
            document.body.style.touchAction = '';
        }

        return () => {
            document.body.style.overflow = 'auto';
            document.body.style.overscrollBehaviorX = '';
            document.body.style.touchAction = '';
        };
    }, [isModalOpen]);

    // Add effect to handle escape key
    useEffect(() => {
        const handleEscapeKey = (event) => {
            if (event.key === 'Escape' && isModalOpen) {
                handleCloseSlider();
            }
        };

        window.addEventListener('keydown', handleEscapeKey);
        return () => window.removeEventListener('keydown', handleEscapeKey);
    }, [isModalOpen]);


    const handleImageLoad = (event, idx) => {
        const img = event.target;
        const container = img.closest('.portrait-img-container-square');

        // Only proceed with text positioning if we're in the grid (container exists)
        if (container) {
            const textElement = container.querySelector('.portrait-name-square');

            if (img && textElement) {
                const imgWidth = img.offsetWidth;
                textElement.style.width = `${imgWidth}px`;
            }
        }

        // Always update image dimensions for modal
        setImageDimensions(prev => ({
            ...prev,
            [idx]: {
                width: img.naturalWidth,
                height: img.naturalHeight
            }
        }));
    };

    const handlePortraitClick = (portrait, index, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        // Open modal first to ensure overlay mounts immediately
        setIsModalOpen(true);
        setInitialSlide(index);
        setActivePortrait(portrait);
        setCurrentSlide(index + 1);
        // reset loader for opening slide until it loads
        setModalImagesLoaded(prev => ({ ...prev, [index]: false }));
        // Add to history when opening slider
        window.history.pushState({ slider: true }, '');
    };

    // Navigate to correct slide when modal opens
    useEffect(() => {
        if (isModalOpen && modalSwiper) {
            setModalReady(false);
            // Use requestAnimationFrame to ensure Swiper is fully initialized
            requestAnimationFrame(() => {
                modalSwiper.slideToLoop(initialSlide, 0);
                // Small delay to ensure slide transition completes
                setTimeout(() => {
                    setModalReady(true);
                }, 50);
            });
        } else {
            setModalReady(false);
        }
    }, [isModalOpen, modalSwiper, initialSlide]);

    const handleCloseSlider = () => {
        setIsModalOpen(false);
        setInitialSlide(0);
        // Remove the history entry when closing slider
        if (window.history.state?.slider) {
            window.history.back();
        }
    };

    return (
        <AnimatePresence mode="wait">
            <motion.div
                className="portraits-container-square"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1 }}
            >
                {(!loading && (!portraits || !Array.isArray(portraits) || portraits.length === 0)) ? (
                    <div>No portraits available</div>
                ) : (
                    <>
                        <Helmet>
                            <title>Portraits • Alex de Brabant</title>
                            <meta name="description" content="Alex de Brabant is a Canadian artist and photographer based in Berlin, working at the intersection of art, culture, design, and ecology." />
                            <link rel="canonical" href="https://alexdebrabant.com/portraits" />
                            <meta property="og:title" content="Portraits • Alex de Brabant" />
                            <meta property="og:description" content="Alex de Brabant is a Canadian artist and photographer based in Berlin, working at the intersection of art, culture, design, and ecology." />
                            <meta property="og:type" content="website" />
                            <meta name="twitter:card" content="summary_large_image" />
                            <meta name="twitter:title" content="Portraits • Alex de Brabant" />
                            <meta name="twitter:description" content="Alex de Brabant is a Canadian artist and photographer based in Berlin, working at the intersection of art, culture, design, and ecology." />
                        </Helmet>
                        <Header
                            pageTitle="Portraits"
                            isPortraitsPage={true}
                            isPortraitsModalOpen={isModalOpen}
                            onClosePortraitsModal={handleCloseSlider}
                            buttons={buttons}
                        />

                        <div className={`portraits-grid-square ${isModalOpen ? 'hidden' : ''}`}>
                            {portraits.map((portrait, idx) => (
                                <div
                                    className="portrait-card-square"
                                    key={idx}
                                    onClick={(e) => handlePortraitClick(portrait, idx, e)}
                                >
                                    <div className="portrait-img-container-square">
                                        {portrait.url ? (
                                            isVideo(portrait) ? (
                                                <video
                                                    className="portrait-img-square"
                                                    autoPlay
                                                    muted
                                                    loop
                                                    playsInline
                                                    draggable={false}
                                                    onLoadedMetadata={(e) => handleImageLoad(e, idx)}
                                                >
                                                    <source src={getVideoUrl(portrait)} type="video/mp4" />
                                                    Your browser does not support the video tag.
                                                </video>
                                            ) : (
                                                <img
                                                    src={getThumbnailUrl(portrait)}
                                                    alt={portrait.title}
                                                    loading="lazy"
                                                    width={getThumbnailDimensions(portrait).width}
                                                    height={getThumbnailDimensions(portrait).height}
                                                    className={`portrait-img-square ${hoveredImageId !== null && hoveredImageId !== idx ? 'grayscale' : ''}`}
                                                    onMouseEnter={() => setHoveredImageId(idx)}
                                                    onMouseLeave={() => setHoveredImageId(null)}
                                                    draggable={false}
                                                    onLoad={(e) => handleImageLoad(e, idx)}
                                                />
                                            )
                                        ) : (
                                            <div className="portrait-img-placeholder">
                                                <span>no image available</span>
                                            </div>
                                        )}
                                        <div className="portrait-name-square">{portrait.title}</div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div
                            className={`modal-overlay ${isModalOpen ? 'open' : ''} ${modalReady ? 'ready' : ''}`}
                            onClick={handleCloseSlider}
                        >
                            {!modalImagesLoaded[currentSlide - 1] && (
                                <div className="loading-container-modal">
                                    <p className="loading-text">loading</p>
                                </div>
                            )}
                            <div
                                className="modal-content"
                                onClick={e => e.stopPropagation()}
                            >
                                <div className="modal-footer">
                                    <p className="modal-footer-text">{activePortrait?.title}</p>
                                    <p className="slide-counter">{currentSlide} of {portraits.length}</p>
                                </div>
                                <Swiper
                                    key={initialSlide}
                                    modules={[Navigation, Keyboard, Mousewheel, Zoom]}
                                    navigation={true}
                                    keyboard={true}
                                    className="modal-swiper"
                                    loop={true}
                                    centeredSlides={true}
                                    initialSlide={initialSlide}
                                    onSwiper={setModalSwiper}
                                    zoom={{ toggle: false }}
                                    touchRatio={0.7}
                                    threshold={5}
                                    longSwipesRatio={0.5}
                                    preventInteractionOnTransition={true}

                                    mousewheel={{
                                        enabled: true,
                                        // forceToAxis: true,
                                        sensitivity: 0.4,
                                        releaseOnEdges: true,
                                        thresholdDelta: 60,
                                        //thresholdTime: 250
                                    }}
                                    speed={600}
                                    lazy={{
                                        loadPrevNext: true,
                                        loadPrevNextAmount: 1,
                                    }}
                                    onSlideChange={(swiper) => {
                                        setCurrentSlide(swiper.realIndex + 1);
                                        setActivePortrait(portraits[swiper.realIndex]);
                                        if (!modalImagesLoaded[swiper.realIndex]) {
                                            setModalImagesLoaded(prev => ({ ...prev, [swiper.realIndex]: false }));
                                        }
                                    }}
                                >
                                    {portraits.map((portrait, index) => (
                                        <SwiperSlide key={index}>
                                            {isVideo(portrait) ? (
                                                <video
                                                    src={getVideoUrl(portrait)}
                                                    autoPlay
                                                    muted
                                                    loop
                                                    playsInline
                                                    draggable={false}
                                                    onLoadedData={() => setModalImagesLoaded(prev => ({ ...prev, [index]: true }))}
                                                />
                                            ) : (
                                                <div className="swiper-zoom-container">
                                                    <img
                                                        /*   src={getImageUrl(portrait)} */
                                                        src={getFullScreenUrl(portrait)}
                                                        alt={portrait.title}
                                                        draggable={false}
                                                        loading="lazy"
                                                        className={`modal-image ${modalImagesLoaded[index] ? 'loaded' : ''}`}
                                                        width={imageDimensions[index]?.width || undefined}
                                                        height={imageDimensions[index]?.height || undefined}
                                                        onLoad={(e) => {
                                                            handleImageLoad(e, index);
                                                            setModalImagesLoaded(prev => ({ ...prev, [index]: true }));
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                            </div>
                        </div>
                    </>
                )}
            </motion.div>
        </AnimatePresence>
    )
}

export default Portraits